import { useState } from "react";
import { useAuth } from "./AuthContext";
import { auth } from "./firebase";
import { browserLocalPersistence, browserSessionPersistence, setPersistence, sendPasswordResetEmail } from "firebase/auth";

const S = {
  page:  { minHeight:"100vh", background:"#f0f2f8", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px", fontFamily:"'DM Sans', system-ui, sans-serif" },
  card:  { background:"#fff", borderRadius:16, border:"1.5px solid rgba(37,99,235,.10)", boxShadow:"0 4px 24px rgba(37,99,235,.08)", padding:"32px", width:"100%", maxWidth:400 },
  input: { width:"100%", background:"#fff", border:"1.5px solid rgba(37,99,235,.12)", borderRadius:8, color:"#0f172a", fontFamily:"'DM Sans', system-ui, sans-serif", fontSize:13, padding:"10px 13px", outline:"none", boxSizing:"border-box" },
};

function friendlyError(code) {
  return ({
    "auth/invalid-credential":    "Incorrect email or password.",
    "auth/user-not-found":        "No account with that email.",
    "auth/wrong-password":        "Incorrect password.",
    "auth/email-already-in-use":  "An account with that email already exists.",
    "auth/weak-password":         "Password must be at least 6 characters.",
    "auth/invalid-email":         "Please enter a valid email address.",
    "auth/popup-closed-by-user":  "Sign-in was cancelled.",
  })[code] || "Something went wrong. Please try again.";
}

export default function AuthScreen() {
  const { signIn, signUp, signInGoogle } = useAuth();
  const [mode,     setMode]     = useState("signin");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [remember, setRemember] = useState(true);
  const [resetSent, setResetSent] = useState(false);

  async function handleSubmit() {
    if (!email || !password) return;
    setLoading(true); setError("");
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      if (mode === "signup") await signUp(email, password);
      else                   await signIn(email, password);
    } catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  }

  async function handleReset() {
    if (!email) { setError("Enter your email address above first."); return; }
    setLoading(true); setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  }

  async function handleGoogle() {
    setLoading(true); setError("");
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      await signInGoogle();
    }
    catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  }

  return (
    <div style={S.page}>
      {/* Wordmark */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:32 }}>
        <div style={{ width:36, height:36, borderRadius:10, background:"#2563eb", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 10px rgba(37,99,235,.35)" }}>
          <div style={{ width:10, height:10, borderRadius:"50%", background:"rgba(255,255,255,.9)" }}/>
        </div>
        <span style={{ fontFamily:"'DM Serif Display', serif", fontSize:28, fontWeight:400, fontStyle:"italic", color:"#0f172a" }}>gather</span>
      </div>

      {/* Hero */}
      <div style={{ textAlign:"center", marginBottom:36, maxWidth:340 }}>
        <h1 style={{ fontFamily:"'DM Serif Display', serif", fontSize:38, fontWeight:400, fontStyle:"italic", color:"#0f172a", lineHeight:1.25, marginBottom:12 }}>
          Less social media.<br/>More social life.
        </h1>
        <p style={{ fontSize:14, color:"#64748b", lineHeight:1.7, fontFamily:"'DM Sans', system-ui, sans-serif" }}>
          gather helps you stay close with the people who matter — not just online, but in real life.
        </p>
      </div>

      <div style={S.card}>
        <h2 style={{ fontFamily:"'DM Serif Display', serif", fontSize:22, fontWeight:400, fontStyle:"italic", color:"#0f172a", marginBottom:6 }}>
          {mode === "signin" ? "Welcome back" : "Create your account"}
        </h2>
        <p style={{ fontSize:13, color:"#64748b", marginBottom:24, lineHeight:1.6, fontFamily:"'DM Sans', system-ui, sans-serif" }}>
          {mode === "signin"
            ? "Good to see you again."
            : "Your people are waiting."}
        </p>

        {/* Google */}
        <button
          onClick={handleGoogle}
          disabled={loading}
          style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, background:"#fff", border:"1.5px solid rgba(37,99,235,.15)", borderRadius:8, padding:"11px", cursor:"pointer", fontFamily:"'DM Sans', system-ui, sans-serif", fontSize:13, fontWeight:500, color:"#334155", marginBottom:16, opacity:loading?.5:1 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <div style={{ flex:1, height:1, background:"rgba(37,99,235,.10)" }}/>
          <span style={{ fontSize:11, color:"#94a3b8", fontWeight:500 }}>or</span>
          <div style={{ flex:1, height:1, background:"rgba(37,99,235,.10)" }}/>
        </div>

        {/* Fields */}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <input
            value={email} onChange={e=>setEmail(e.target.value)}
            placeholder="Email address" type="email" style={S.input}
          />
          <input
            value={password} onChange={e=>setPassword(e.target.value)}
            placeholder="Password" type="password" style={S.input}
            onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
          />
        </div>

        {mode==="signin"&&!resetSent&&(
          <p style={{ fontSize:12, color:"#64748b", textAlign:"right", marginTop:8, fontFamily:"'DM Sans', system-ui, sans-serif" }}>
            <span onClick={handleReset} style={{ color:"#2563eb", cursor:"pointer", fontWeight:500 }}>Forgot password?</span>
          </p>
        )}

        {resetSent&&(
          <div style={{ background:"#f0fdf4", border:"1.5px solid #86efac", borderRadius:8, padding:"12px 14px", marginTop:10 }}>
            <p style={{ fontSize:13, fontWeight:600, color:"#166534", fontFamily:"'DM Sans', system-ui, sans-serif", marginBottom:2 }}>✓ Reset link sent!</p>
            <p style={{ fontSize:12, color:"#166534", fontFamily:"'DM Sans', system-ui, sans-serif" }}>Check your email and follow the link to set a new password. Come back here to sign in.</p>
            <span onClick={()=>{ setResetSent(false); setError(""); }} style={{ fontSize:12, color:"#2563eb", cursor:"pointer", fontWeight:500, display:"inline-block", marginTop:6 }}>← Back to sign in</span>
          </div>
        )}

        {error && (
          <p style={{ fontSize:12, color:"#dc2626", marginTop:10, fontFamily:"'DM Sans', system-ui, sans-serif" }}>{error}</p>
        )}

        {/* Remember me */}
        <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:14, cursor:"pointer" }}>
          <div onClick={()=>setRemember(r=>!r)} style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${remember?"#2563eb":"rgba(37,99,235,.25)"}`, background:remember?"#2563eb":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .15s" }}>
            {remember && <span style={{ color:"#fff", fontSize:11, fontWeight:700, lineHeight:1 }}>✓</span>}
          </div>
          <span style={{ fontSize:12, color:"#64748b", fontFamily:"'DM Sans', system-ui, sans-serif", userSelect:"none" }}>Keep me signed in</span>
        </label>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading || !email || !password}
          style={{ width:"100%", background:"#2563eb", color:"#fff", border:"none", borderRadius:8, padding:"11px", cursor:(loading||!email||!password)?"not-allowed":"pointer", fontFamily:"'DM Sans', system-ui, sans-serif", fontSize:13, fontWeight:600, marginTop:16, opacity:(loading||!email||!password)?.55:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
        >
          {loading
            ? <><span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block" }}/> Working…</>
            : mode === "signin" ? "Sign in" : "Create account"}
        </button>

        {/* Toggle */}
        <p style={{ fontSize:12, color:"#64748b", textAlign:"center", marginTop:16, fontFamily:"'DM Sans', system-ui, sans-serif" }}>
          {mode === "signin"
            ? <> Don't have an account?{" "}<span onClick={()=>{setMode("signup");setError("");setResetSent(false);}} style={{ color:"#2563eb", cursor:"pointer", fontWeight:500 }}>Sign up</span></>
            : <> Already have an account?{" "}<span onClick={()=>{setMode("signin");setError("");setResetSent(false);}} style={{ color:"#2563eb", cursor:"pointer", fontWeight:500 }}>Sign in</span></>}
        </p>
      </div>

      <p style={{ fontSize:11, color:"#94a3b8", marginTop:24, textAlign:"center" }}>
        Less scrolling. More showing up.
      </p>
      <p style={{ fontSize:11, color:"#94a3b8", marginTop:10, textAlign:"center" }}>
        <a href="/privacy" style={{ color:"#94a3b8", textDecoration:"underline" }}>Privacy Policy</a>
        {" · "}
        <a href="mailto:hello@heygather.app" style={{ color:"#94a3b8", textDecoration:"underline" }}>Contact</a>
      </p>
    </div>
  );
}
